import { useState, useEffect, useCallback } from 'react';
import { useMuseum } from '@/features/museum/context/MuseumContext';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { collection, query, getDocs, addDoc, updateDoc, doc, orderBy, deleteDoc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS } from '@/shared/constants';
import { Work } from '@/shared/types';
import { logger } from '@/shared/utils/logger';
import { getMuseumLanguages } from '@/shared/utils/languageUtils';

export interface CreateWorkData {
  name: Record<string, string>;
  description: Record<string, string>;
  audioUrl: Record<string, string>;
  imageUrl: string | null;
  availableLanguages: string[];
  status: Record<string, 'draft' | 'published'>; // Statut par langue
  floor?: Record<string, string>; // Multi-langue: { fr: "Rez-de-chaussée", en: "Ground floor" }
  room?: Record<string, string>; // Multi-langue: { fr: "Salle 1", en: "Room 1" }
  pillNumber?: number | string | null;
  order: number;
  durationTimeInSeconds?: Record<string, number>; // Par langue
}

export const useWorks = (exhibitionId: string | undefined) => {
  const { museum } = useMuseum();
  const [works, setWorks] = useState<Work[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorks = useCallback(async () => {
    if (!museum?.id || !exhibitionId) {
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    if (!db) {
      setError('Firebase DB not initialized');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const worksRef = collection(
        db,
        FIRESTORE_COLLECTIONS.MUSEUMS,
        museum.id,
        FIRESTORE_COLLECTIONS.EXHIBITIONS,
        exhibitionId,
        FIRESTORE_COLLECTIONS.WORKS
      );
      const q = query(worksRef, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const worksData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Work[];
      setWorks(worksData);
    } catch (err) {
      logger.error('Erreur lors de la récupération des œuvres:', err);
      setError('Erreur lors de la récupération des œuvres');
    } finally {
      setLoading(false);
    }
  }, [museum, exhibitionId]);

  const createWork = useCallback(
    async (data: CreateWorkData): Promise<string | null> => {
      if (!museum?.id || !exhibitionId) {
        setError('Musée ou exposition non trouvé');
        return null;
      }

      const db = getFirebaseDb();
      if (!db) {
        setError('Firebase DB not initialized');
        return null;
      }

      try {
        setError(null);
        const worksRef = collection(
          db,
          FIRESTORE_COLLECTIONS.MUSEUMS,
          museum.id,
          FIRESTORE_COLLECTIONS.EXHIBITIONS,
          exhibitionId,
          FIRESTORE_COLLECTIONS.WORKS
        );
        // Préparer createdAt et updatedAt par langue (utiliser les langues disponibles depuis data.name ou du musée)
        const languages = data.name ? Object.keys(data.name) : getMuseumLanguages(museum);
        const now = new Date();
        const createdAtByLang: Record<string, Date> = {};
        const updatedAtByLang: Record<string, Date> = {};
        languages.forEach((lang) => {
          createdAtByLang[lang] = now;
          updatedAtByLang[lang] = now;
        });

        const workData = {
          ...data,
          museumId: museum.id,
          exhibitionId,
          createdAt: createdAtByLang,
          updatedAt: updatedAtByLang,
        };
        const docRef = await addDoc(worksRef, workData);
        await fetchWorks(); // Recharger la liste
        return docRef.id;
      } catch (err) {
        logger.error('Erreur lors de la création de l\'œuvre:', err);
        setError('Erreur lors de la création de l\'œuvre');
        return null;
      }
    },
    [museum, exhibitionId, fetchWorks]
  );

  const updateWork = useCallback(
    async (id: string, data: Partial<Work>): Promise<boolean> => {
      if (!museum?.id || !exhibitionId) {
        setError('Musée ou exposition non trouvé');
        return false;
      }

      const db = getFirebaseDb();
      if (!db) {
        setError('Firebase DB not initialized');
        return false;
      }

      try {
        setError(null);
        const workRef = doc(
          db,
          FIRESTORE_COLLECTIONS.MUSEUMS,
          museum.id,
          FIRESTORE_COLLECTIONS.EXHIBITIONS,
          exhibitionId,
          FIRESTORE_COLLECTIONS.WORKS,
          id
        );
        // Préparer updatedAt par langue (conserver createdAt existant)
        const languages = data.name ? Object.keys(data.name) : getMuseumLanguages(museum);
        const now = new Date();
        const updatedAtByLang: Record<string, Date> = {};
        languages.forEach((lang) => {
          updatedAtByLang[lang] = now;
        });

        await updateDoc(workRef, {
          ...data,
          updatedAt: updatedAtByLang,
        });
        await fetchWorks(); // Recharger la liste
        return true;
      } catch (err) {
        logger.error('Erreur lors de la mise à jour de l\'œuvre:', err);
        setError('Erreur lors de la mise à jour de l\'œuvre');
        return false;
      }
    },
    [museum, exhibitionId, fetchWorks]
  );

  const deleteWork = useCallback(
    async (id: string): Promise<boolean> => {
      if (!museum?.id || !exhibitionId) {
        setError('Musée ou exposition non trouvé');
        return false;
      }

      const db = getFirebaseDb();
      if (!db) {
        setError('Firebase DB not initialized');
        return false;
      }

      try {
        setError(null);
        const workRef = doc(
          db,
          FIRESTORE_COLLECTIONS.MUSEUMS,
          museum.id,
          FIRESTORE_COLLECTIONS.EXHIBITIONS,
          exhibitionId,
          FIRESTORE_COLLECTIONS.WORKS,
          id
        );
        await deleteDoc(workRef);
        await fetchWorks(); // Recharger la liste
        return true;
      } catch (err) {
        logger.error('Erreur lors de la suppression de l\'œuvre:', err);
        setError('Erreur lors de la suppression de l\'œuvre');
        return false;
      }
    },
    [museum, exhibitionId, fetchWorks]
  );

  useEffect(() => {
    fetchWorks();
  }, [fetchWorks]);

  return {
    works,
    loading,
    error,
    createWork,
    updateWork,
    deleteWork,
    refetch: fetchWorks,
  };
};

